import type { Vector2Tuple, Vector3Tuple } from 'three';

import type { BoxWithLabel, PCDData } from '@/type';

import { getPoint } from '../pcd';

type Mat3 = [Vector3Tuple, Vector3Tuple, Vector3Tuple];
export type AABB = { min: Vector3Tuple; max: Vector3Tuple };
export type OBB = {
  c: Vector3Tuple;
  a: Mat3;
  e: Vector3Tuple;
};
type OBBPre = OBB & { zMin: number; zMax: number };
type LeafEntry = {
  min: Vector3Tuple;
  max: Vector3Tuple;
  index: number;
  center: Vector3Tuple;
};
type LeafNode = {
  leaf: true;
  children: LeafEntry[];
  bbox: AABB;
  height: 1;
};
type InternalNode = {
  leaf: false;
  children: Node[];
  bbox: AABB;
  height: number; // 자식 높이 + 1
};
type Node = LeafNode | InternalNode;
type Axis = 0 | 1 | 2;
type PseudoLeaf = {
  min: Vector3Tuple;
  max: Vector3Tuple;
  start: number;
  end: number;
};

const EPS = 1e-8 as const;

const assertVector3 = (...value: Vector3Tuple[]) => {
  if (value.some(v => v.length !== 3)) {
    console.warn('The parameter must be of type `Vector3Tuple`');
  }

  return value.map(v => v.slice(0, 3) as Vector3Tuple);
};

const dot = (u: Vector3Tuple, v: Vector3Tuple) => {
  [u, v] = assertVector3(u, v);

  return u.reduce((sum, cur, i) => sum + cur * v[i], 0);
};
const sub = (a: Vector3Tuple, b: Vector3Tuple) => {
  [a, b] = assertVector3(a, b);

  return a.map((v, i) => v - b[i]) as Vector3Tuple;
};

const vMinOrMax =
  (minOrMax: Math['min'] | Math['max']) =>
  (a: Vector3Tuple, b: Vector3Tuple) => {
    [a, b] = assertVector3(a, b);

    return a.slice(0, 3).map((v, i) => minOrMax(v, b[i])) as Vector3Tuple;
  };

const vMin = vMinOrMax(Math.min);
const vMax = vMinOrMax(Math.max);

const aabbOverlap = (A: AABB, B: AABB) =>
  !(
    A.max[0] < B.min[0] ||
    A.max[1] < B.min[1] ||
    A.max[2] < B.min[2] ||
    B.max[0] < A.min[0] ||
    B.max[1] < A.min[1] ||
    B.max[2] < A.min[2]
  );

const eulerXYZToMat3 = ([rx, ry, rz]: Vector3Tuple): Mat3 => {
  const [cx, cy, cz] = [rx, ry, rz].map(Math.cos);
  const [sx, sy, sz] = [rx, ry, rz].map(Math.sin);

  return [
    [cy * cz, -cy * sz, sy],
    [cx * sz + cz * sx * sy, cx * cz - sx * sy * sz, -cy * sx],
    [sx * sz - cx * cz * sy, cz * sx + cx * sy * sz, cx * cy],
  ];
};
const axesFromR = (R: Mat3): [Vector3Tuple, Vector3Tuple, Vector3Tuple] => {
  const ax: Vector3Tuple = [R[0][0], R[1][0], R[2][0]];
  const ay: Vector3Tuple = [R[0][1], R[1][1], R[2][1]];
  const az: Vector3Tuple = [R[0][2], R[1][2], R[2][2]];

  return [ax, ay, az];
};

export const obbToAabb = (box: BoxWithLabel): AABB => {
  const R = eulerXYZToMat3(box.rotation as Vector3Tuple);
  const [ex, ey, ez] = box.dimension.map(v => v / 2);

  const hx =
    Math.abs(R[0][0]) * ex + Math.abs(R[0][1]) * ey + Math.abs(R[0][2]) * ez;
  const hy =
    Math.abs(R[1][0]) * ex + Math.abs(R[1][1]) * ey + Math.abs(R[1][2]) * ez;
  const hz =
    Math.abs(R[2][0]) * ex + Math.abs(R[2][1]) * ey + Math.abs(R[2][2]) * ez;

  const [cx, cy, cz] = box.position;

  return {
    min: [cx - hx, cy - hy, cz - hz],
    max: [cx + hx, cy + hy, cz + hz],
  };
};

export const centerOfAABB = (b: AABB) =>
  b.max.map((max, i) => (max + b.min[i]) / 2) as Vector3Tuple;

const bboxFromFactory =
  <T>(callback: (item: T) => { min: Vector3Tuple; max: Vector3Tuple }) =>
  (list: T[]): AABB => {
    let mn: Vector3Tuple = [Infinity, Infinity, Infinity];
    let mx: Vector3Tuple = [-Infinity, -Infinity, -Infinity];

    for (const item of list) {
      const { min, max } = callback(item);

      mn = vMin(mn, min);
      mx = vMax(mx, max);
    }

    return { min: mn, max: mx };
  };

const bboxFromLeafEntryList = bboxFromFactory<LeafEntry>(entry => ({
  min: entry.min,
  max: entry.max,
}));

const bboxFromChildNodeList = bboxFromFactory<Node>(node => ({
  min: node.bbox.min,
  max: node.bbox.max,
}));

const makeLeafNode = (entryList: LeafEntry[]): LeafNode => ({
  leaf: true,
  children: entryList,
  bbox: bboxFromLeafEntryList(entryList),
  height: 1,
});

const makeInternalNode = (children: Node[]): InternalNode => ({
  leaf: false,
  children,
  bbox: bboxFromChildNodeList(children),
  height: (children[0]?.height ?? 0) + 1,
});

const makeLevelFromLeaf = (
  entryList: LeafEntry[],
  maxEntryLength: number,
  axis: Axis,
) => {
  entryList.sort((a, b) => a.center[axis] - b.center[axis]);

  const nodeList: LeafNode[] = [];

  for (let i = 0; i < entryList.length; i += maxEntryLength) {
    nodeList.push(makeLeafNode(entryList.slice(i, i + maxEntryLength)));
  }

  return nodeList;
};

const makeLevelFromNodeList = (
  nodeListIn: Node[],
  maxEntryLength: number,
  axis: Axis,
) => {
  nodeListIn.sort(
    (a, b) => centerOfAABB(a.bbox)[axis] - centerOfAABB(b.bbox)[axis],
  );

  const nodeList: InternalNode[] = [];

  for (let i = 0; i < nodeListIn.length; i += maxEntryLength) {
    const group = nodeListIn.slice(i, i + maxEntryLength);

    nodeList.push(makeInternalNode(group));
  }

  return nodeList;
};

export const buildRTree = (boxList: BoxWithLabel[], maxEntryLength = 8) => {
  const entryList: LeafEntry[] = boxList.map((b, i) => {
    const a = obbToAabb(b);
    const c = centerOfAABB(a);

    return { min: a.min, max: a.max, center: c, index: i };
  });

  if (entryList.length === 0) return makeLeafNode([]);
  if (entryList.length <= maxEntryLength) return makeLeafNode(entryList);

  let level: Node[] = makeLevelFromLeaf(entryList, maxEntryLength, 0);
  let axis: Axis = 1;

  while (level.length > maxEntryLength) {
    level = makeLevelFromNodeList(level, maxEntryLength, axis);
    axis = ((axis + 1) % 3) as Axis;
  }

  return makeInternalNode(level);
};

export const queryIndices = (root: Node, query: AABB): number[] => {
  const out: number[] = [];
  const stack: Node[] = [root];

  while (stack.length) {
    const node = stack.pop()!;

    if (!aabbOverlap(node.bbox, query)) continue;

    if (node.leaf) {
      node.children.forEach(({ min, max, index }) => {
        aabbOverlap({ min, max }, query) && out.push(index);
      });
    } else {
      node.children.forEach(child => stack.push(child));
    }
  }

  return out;
};

const toOBB = (box: BoxWithLabel): OBB => {
  const R = eulerXYZToMat3(box.rotation as Vector3Tuple);
  const a = axesFromR(R);
  const e = box.dimension.map(v => v / 2) as Vector3Tuple;

  return { c: box.position, a, e };
};

export const buildObbPre = (boxList: BoxWithLabel[]): OBBPre[] => {
  const out: OBBPre[] = [];

  for (let i = 0; i < boxList.length; i++) {
    const obb = toOBB(boxList[i]);
    const [ax, ay, az] = obb.a;
    const [ex, ey, ez] = obb.e;
    const hz =
      Math.abs(ax[2]) * ex + Math.abs(ay[2]) * ey + Math.abs(az[2]) * ez;
    const zMin = obb.c[2] - hz;
    const zMax = obb.c[2] + hz;

    out[i] = { ...obb, zMin, zMax };
  }

  return out;
};

export const aabbFromBucket = (
  pointList: Vector3Tuple[],
  zKey: number,
): AABB => {
  let [minX, maxX] = [Infinity, -Infinity];
  let [minY, maxY] = [Infinity, -Infinity];

  pointList.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  });

  return { min: [minX, minY, zKey], max: [maxX, maxY, zKey + 1 - EPS] };
};

export const pointInsideOBB = (
  px: number,
  py: number,
  pz: number,
  b: OBBPre,
): boolean => {
  const ux = px - b.c[0];
  const uy = py - b.c[1];
  const uz = pz - b.c[2];
  const ax = b.a[0];
  const ay = b.a[1];
  const az = b.a[2];
  const lx = ux * ax[0] + uy * ax[1] + uz * ax[2];
  const ly = ux * ay[0] + uy * ay[1] + uz * ay[2];
  const lz = ux * az[0] + uy * az[1] + uz * az[2];

  return (
    Math.abs(lx) <= b.e[0] + EPS &&
    Math.abs(ly) <= b.e[1] + EPS &&
    Math.abs(lz) <= b.e[2] + EPS
  );
};

export const suggestXYSpan = (boxList: BoxWithLabel[]): Vector2Tuple => {
  const xs: number[] = [];
  const ys: number[] = [];

  boxList.forEach(b => {
    xs.push(b.dimension[0] * 0.5);
    ys.push(b.dimension[1] * 0.5);
  });

  const median = (arr: number[]) => {
    if (arr.length === 0) return 1;

    const s = arr.slice().sort((a, b) => a - b);
    const m = s[Math.floor(s.length / 2)];

    return m;
  };

  const hx = median(xs);
  const hy = median(ys);

  // 박스가 어느 정도 들어맞도록 여유 계수(예: 4) 부여
  return [hx * 4, hy * 4];
};

export const buildPseudoLeaveList = (
  pcdPointList: PCDData['pointList'],
  param?: { maxPointPerBlock?: number; maxXYSpan?: [number, number] },
): PseudoLeaf[] => {
  const count = pcdPointList.length;

  if (count === 0) return [];

  const { maxPointPerBlock = 2048, maxXYSpan = [Infinity, Infinity] } =
    param ?? {};
  const [maxSpanX, maxSpanY] = maxXYSpan;

  const leaveList: PseudoLeaf[] = [];
  let start = 0;
  let [minX, minY, minZ] = [Infinity, Infinity, Infinity];
  let [maxX, maxY, maxZ] = [-Infinity, -Infinity, -Infinity];

  const commit = (endExclusive: number) => {
    if (
      isNaN(minX) ||
      isNaN(minY) ||
      isNaN(minZ) ||
      isNaN(maxX) ||
      isNaN(maxY) ||
      isNaN(maxZ)
    )
      return;

    leaveList.push({
      min: [minX, minY, minZ],
      max: [maxX, maxY, maxZ],
      start,
      end: endExclusive,
    });
  };

  for (let i = 0; i < count; ) {
    start = i;
    minX = minY = minZ = Infinity;
    maxX = maxY = maxZ = -Infinity;

    let blkLen = 0;

    while (i < count) {
      const [x, y, z] = getPoint(pcdPointList, i);
      const nextMinX = Math.min(minX, x);
      const nextMinY = Math.min(minY, y);
      const nextMinZ = Math.min(minZ, z);
      const nextMaxX = Math.max(maxX, x);
      const nextMaxY = Math.max(maxY, y);
      const nextMaxZ = Math.max(maxZ, z);

      const tooMany = blkLen >= maxPointPerBlock;
      const tooWide =
        nextMaxX - nextMinX > maxSpanX || nextMaxY - nextMinY > maxSpanY;

      if ((tooMany || tooWide) && blkLen > 0) break;

      minX = nextMinX;
      minY = nextMinY;
      minZ = nextMinZ;
      maxX = nextMaxX;
      maxY = nextMaxY;
      maxZ = nextMaxZ;
      blkLen++;
      i++;
    }

    commit(i);
  }

  return leaveList;
};

export function countPointPerBoxFromBlock({
  boxList,
  pcdPointList,
  option,
}: {
  boxList: BoxWithLabel[];
  pcdPointList: PCDData['pointList'];
  option?: { maxPointPerBlock?: number; maxXYSpan?: [number, number] };
}) {
  const N = boxList.length;
  const result = boxList.reduce(
    (acc, box) => ({
      ...acc,
      [box.id]: 0,
    }),
    {} as Dictionary<number>,
  );

  if (N === 0 || pcdPointList.length === 0) return result;

  const xySpan = option?.maxXYSpan ?? suggestXYSpan(boxList);
  const leaveList = buildPseudoLeaveList(pcdPointList, {
    maxPointPerBlock: option?.maxPointPerBlock,
    maxXYSpan: xySpan,
  });
  const pre = buildObbPre(boxList);
  const root = buildRTree(boxList);

  for (let li = 0; li < leaveList.length; li++) {
    const leaf = leaveList[li];
    const candidateList = queryIndices(root, {
      min: leaf.min,
      max: leaf.max,
    });

    if (candidateList.length === 0) continue;

    for (let ci = 0; ci < candidateList.length; ci++) {
      const bi = candidateList[ci];
      const b = pre[bi];

      if (leaf.max[2] < b.zMin || leaf.min[2] > b.zMax) continue;

      for (let pid = leaf.start; pid < leaf.end; pid++) {
        const [px, py, pz] = getPoint(pcdPointList, pid);

        if (pz < b.zMin || pz > b.zMax) continue;
        if (pointInsideOBB(px, py, pz, b)) {
          result[boxList[bi].id] = (result[boxList[bi].id] ?? 0) + 1;
        }
      }
    }
  }

  return result;
}

export const findOverlappingPairListRTree = ({
  boxList,
  overlaps: _overlaps = overlaps,
  maxEntryLength = 8,
}: {
  boxList: BoxWithLabel[];
  overlaps?: (a: BoxWithLabel, b: BoxWithLabel) => boolean;
  maxEntryLength?: number;
}) => {
  const root = buildRTree(boxList, maxEntryLength);
  const pairList: [BoxWithLabel['id'], BoxWithLabel['id']][] = [];

  for (let i = 0; i < boxList.length; i++) {
    const aabb = obbToAabb(boxList[i]);
    const candidateList = queryIndices(root, aabb);

    for (const j of candidateList) {
      if (j <= i) continue;
      if (_overlaps(boxList[i], boxList[j]))
        pairList.push([boxList[i].id, boxList[j].id]);
    }
  }

  return pairList;
};

export const overlaps = (A: BoxWithLabel, B: BoxWithLabel) => {
  const obbA = toOBB(A);
  const obbB = toOBB(B);

  const { c: cA, a: Aaxes, e: eA } = obbA;
  const { c: cB, a: Baxes, e: eB } = obbB;

  const R: number[][] = [[], [], []];
  const absR: number[][] = [[], [], []];

  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      const val = dot(Aaxes[i], Baxes[j]);

      R[i][j] = val;
      absR[i][j] = Math.abs(val) + EPS;
    }
  }

  const t = sub(cB, cA);
  const tA: number[] = [dot(t, Aaxes[0]), dot(t, Aaxes[1]), dot(t, Aaxes[2])];

  for (let i = 0; i < 3; i++) {
    const ra = eA[i];
    const rb = eB[0] * absR[i][0] + eB[1] * absR[i][1] + eB[2] * absR[i][2];

    if (Math.abs(tA[i]) > ra + rb) return false;
  }

  for (let j = 0; j < 3; j++) {
    const ra = eA[0] * absR[0][j] + eA[1] * absR[1][j] + eA[2] * absR[2][j];
    const tB = Math.abs(tA[0] * R[0][j] + tA[1] * R[1][j] + tA[2] * R[2][j]);
    const rb = eB[j];

    if (tB > ra + rb) return false;
  }

  for (let i = 0; i < 3; i++) {
    const i1 = (i + 1) % 3;
    const i2 = (i + 2) % 3;

    for (let j = 0; j < 3; j++) {
      const j1 = (j + 1) % 3;
      const j2 = (j + 2) % 3;

      const ra = eA[i1] * absR[i2][j] + eA[i2] * absR[i1][j];
      const rb = eB[j1] * absR[i][j2] + eB[j2] * absR[i][j1];

      const tVal = Math.abs(tA[i2] * R[i1][j] - tA[i1] * R[i2][j]);

      if (tVal > ra + rb) return false;
    }
  }

  return true;
};
